import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  MessageCircleQuestion, Bookmark, BarChart3, Building2, Users, Network, Menu, X,
  Activity, Stethoscope, Hospital, PieChart, ChevronDown, Fingerprint, LogOut, ShieldCheck, UserCircle2, Home, HeartPulse, Layers,
} from 'lucide-react';
import GlobalEntitySearch from './GlobalEntitySearch';
import { useAuth } from '../context/AuthContext';

// Grouped nav (5 top-level items). Each "group" expands into its own dropdown.
const NAV = [
  {
    label: 'Facilities',
    icon: Building2,
    children: [
      { to: '/facilities',           label: 'Facilities',          icon: Building2, desc: 'Directory of every CMS-certified SNF' },
      { to: '/rankings',             label: 'Rankings',            icon: BarChart3, desc: 'Best & worst facilities by metric' },
      { to: '/census-intelligence',  label: 'Census & Payer Mix',  icon: PieChart,  desc: 'Estimated census + payer mix' },
    ],
  },
  {
    label: 'Owners',
    icon: Users,
    children: [
      { to: '/owners', label: 'Owners', icon: Users,    desc: 'Owner directory & profiles' },
      { to: '/chains', label: 'Chains', icon: Network,  desc: 'Multi-facility chain analytics' },
    ],
  },
  {
    label: 'Intelligence',
    icon: Activity,
    children: [
      { to: '/physician-intelligence', label: 'Physicians',          icon: Stethoscope, desc: 'Part B + DAC alignment' },
      { to: '/hospital-intelligence',  label: 'Hospitals',           icon: Hospital,    desc: 'Hospital × SNF referral network' },
      { to: '/home-health',            label: 'Home Health',         icon: Home,        desc: 'HHA directory & ratings' },
      { to: '/hospice',                label: 'Hospice',             icon: HeartPulse,  desc: 'Hospice agencies directory' },
      { to: '/continuum-of-care',      label: 'Continuum of Care',   icon: Layers,      desc: 'Cross-overlap SNF / Hospital / HHA / Hospice + market share' },
      { to: '/managed-care',           label: 'Managed Care',        icon: Activity,    desc: 'MA penetration, plans, payers & contract targets' },
      { to: '/entity-intel',           label: 'Entity Intelligence', icon: Fingerprint, desc: 'NPPES identifiers, DBAs, taxonomies' },
    ],
  },
  { to: '/ask',       label: 'Ask AI',    icon: MessageCircleQuestion },
  { to: '/watchlist', label: 'Watchlist', icon: Bookmark },
];

const Logo = () => (
  <Link to="/home" className="flex items-center gap-2 group">
    <span className="font-display text-[22px] font-semibold text-[#1A2622] tracking-tight">Piece of Cake</span>
    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7A857F] border-l border-[#D4C9AF] pl-2 ml-0.5">LTC</span>
  </Link>
);

function isActive(pathname, item) {
  if (item.to) return pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to));
  if (item.children) return item.children.some((c) => pathname === c.to || pathname.startsWith(c.to));
  return false;
}

function NavGroup({ group, active, onNavigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const Icon = group.icon;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((x) => !x)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] transition-colors duration-150 ${active ? 'text-[#1A2622] font-semibold bg-[#F0E7D2]' : 'text-[#3A4A44] hover:text-[#1A2622] hover:bg-[#F5EFDD]'}`}
        data-testid={`nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Icon size={14} strokeWidth={2} className="text-[#4A7C5E]" />
        <span>{group.label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-[#FBF6EC] border border-[#E5DCC4] rounded-lg shadow-lg overflow-hidden z-50" data-testid={`nav-dropdown-${group.label.toLowerCase()}`}>
          {group.children.map((c) => {
            const CIcon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                onClick={() => { setOpen(false); onNavigate && onNavigate(); }}
                className="flex items-start gap-2.5 px-3.5 py-2.5 hover:bg-[#F5EFDD] border-b last:border-b-0 border-[#F0E7D2]"
              >
                <CIcon size={15} className="text-[#4A7C5E] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[13px] font-semibold text-[#1A2622] leading-tight">{c.label}</div>
                  {c.desc && <div className="text-[11px] text-[#7A857F] mt-0.5 leading-snug">{c.desc}</div>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 transition-all duration-200 ${scrolled ? 'bg-[#FBF6EC]/90 backdrop-blur border-b border-[#E5DCC4]' : 'bg-[#FBF6EC] border-b border-transparent'}`}>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-0.5">
          {NAV.map((item) => {
            const active = isActive(location.pathname, item);
            if (item.children) {
              return <NavGroup key={item.label} group={item} active={active} />;
            }
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] transition-colors duration-150 ${active ? 'text-[#1A2622] font-semibold bg-[#F0E7D2]' : 'text-[#3A4A44] hover:text-[#1A2622] hover:bg-[#F5EFDD]'}`}
              >
                <Icon size={14} strokeWidth={2} className="text-[#4A7C5E]" />
                <span>{item.label}</span>
              </Link>
            );
          })}
          <div className="ml-2 pl-2 border-l border-[#E5DCC4]">
            <GlobalEntitySearch />
          </div>
          <UserMenu />
        </nav>
        <button onClick={() => setOpen(!open)} className="md:hidden p-2 rounded-md hover:bg-[#F0E7D2]" data-testid="nav-mobile-toggle">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#E5DCC4] bg-[#FBF6EC] px-6 py-3" data-testid="nav-mobile-panel">
          <MobileUserSummary onNavigate={() => setOpen(false)} />
          {NAV.map((item) => {
            if (item.children) {
              return (
                <div key={item.label} className="py-2">
                  <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F] font-semibold mb-1">{item.label}</div>
                  {item.children.map((c) => {
                    const CIcon = c.icon;
                    return (
                      <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="flex items-center gap-3 py-2 pl-2 text-sm text-[#1A2622]">
                        <CIcon size={15} className="text-[#4A7C5E]" /> {c.label}
                      </Link>
                    );
                  })}
                </div>
              );
            }
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="flex items-center gap-3 py-3 text-sm text-[#1A2622]">
                <Icon size={16} className="text-[#4A7C5E]" /> {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </header>
  );
}


function UserMenu() {
  const { user, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  // Signed-out state: show "Sign in" pill — unless showcase mode is on.
  if (!user) {
    if (process.env.REACT_APP_AUTH_BYPASS === 'true') return null;
    return (
      <div className="ml-2 flex items-center gap-2">
        <Link to="/login" className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] font-medium hover:bg-[#2D5F4F]" data-testid="nav-signin">
          <UserCircle2 size={13} /> Sign in
        </Link>
      </div>
    );
  }
  // Signed in — no more donation pill in the chrome.
  const upgradePill = null;

  const initials = (user.name || `${user.first_name || ''} ${user.last_name || ''}`)
    .split(' ').filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || 'U';

  return (
    <div ref={ref} className="ml-2 relative flex items-center gap-2">
      {upgradePill}
      <button onClick={() => setOpen((s) => !s)} className="inline-flex items-center gap-1.5 h-9 pl-1.5 pr-2 rounded-md border border-[#D4C9AF] bg-white hover:border-[#4A7C5E]" data-testid="nav-user-menu">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-md object-cover" />
        ) : (
          <span className="w-6 h-6 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[10.5px] font-semibold flex items-center justify-center">{initials}</span>
        )}
        <span className="text-[12.5px] text-[#1A2622] max-w-[110px] truncate">{user.first_name || user.email}</span>
        <ChevronDown size={12} className="text-[#7A857F]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-60 bg-white border border-[#E5DCC4] rounded-lg shadow-xl z-50 overflow-hidden" data-testid="nav-user-popover">
          <div className="px-3.5 py-2.5 border-b border-[#F0E7D2]">
            <div className="text-[13px] font-semibold text-[#1A2622] truncate">{user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim()}</div>
            <div className="text-[11.5px] text-[#7A857F] truncate">{user.email}</div>
            {isAdmin && <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#4A7C5E] font-semibold"><ShieldCheck size={10} /> Admin</div>}
          </div>
          <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3.5 py-2.5 text-[13px] text-[#1A2622] hover:bg-[#F5EFDD]" data-testid="nav-account">
            <UserCircle2 size={13} className="text-[#34538F]" /> Account
          </Link>
          {isAdmin && (
            <Link to="/admin/access-codes" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3.5 py-2.5 text-[13px] text-[#1A2622] hover:bg-[#F5EFDD]" data-testid="nav-admin-access-codes">
              <ShieldCheck size={13} className="text-[#A06628]" /> Access codes
            </Link>
          )}
          <button onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
            className="w-full text-left flex items-center gap-2 px-3.5 py-2.5 text-[13px] text-[#1A2622] hover:bg-[#F5EFDD] border-t border-[#F0E7D2]" data-testid="nav-logout">
            <LogOut size={13} className="text-[#A06628]" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MobileUserSummary({ onNavigate }) {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    if (process.env.REACT_APP_AUTH_BYPASS === 'true') return null;
    return (
      <Link to="/login" onClick={onNavigate} className="flex items-center gap-3 py-2.5 mb-2 text-sm text-[#1A2622] border-b border-[#E5DCC4]">
        <UserCircle2 size={16} className="text-[#4A7C5E]" /> Sign in
      </Link>
    );
  }
  return (
    <div className="py-2 mb-2 border-b border-[#E5DCC4]">
      <div className="text-[13px] font-semibold text-[#1A2622]">{user.name || user.email}</div>
      <div className="text-[11.5px] text-[#7A857F]">{user.email}</div>
      <div className="mt-2 flex items-center gap-2">
        {isAdmin && <Link to="/admin/access-codes" onClick={onNavigate} className="text-[12px] text-[#A06628] inline-flex items-center gap-1 min-h-[32px] py-1"><ShieldCheck size={12} /> Access codes</Link>}
        <button onClick={async () => { await logout(); onNavigate(); navigate('/login', { replace: true }); }} className="text-[12px] text-[#A06628] inline-flex items-center gap-1 min-h-[32px] py-1"><LogOut size={12} /> Sign out</button>
      </div>
    </div>
  );
}
